import React, { useEffect, useMemo, useState, useRef } from 'react';
import Modal from '../../../../../common/Modal';
import { ImEye } from 'react-icons/im';
import { HiPlus } from 'react-icons/hi';
import { Button } from '../../../../../common/index';
import { Coupon, Company } from '../../../../../types/generated.types';
import { useCouponsManageContext, CouponsManageProvider, CouponWithState } from '../../../../../context/CouponsManageContext';
import { useConfirmationModalContext } from '../../../../../context/ConfirmationModalContext';
import { usePreviewContext } from '../../../../../context/PreviewContext';
import { ManageMode } from '../../../../../types/types';
import CouponCard from './CouponCard';
import LoadingCoupon from './LoadingCoupon';
import { sortCouponsByPosition } from './utils/couponUtils';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { HiSparkles, HiXMark } from 'react-icons/hi2';
import { FaRegFloppyDisk } from 'react-icons/fa6';

interface CouponsManageProps {
  isOpen: boolean;
  onClose: () => void;
  quantity: number;
  company: Company;
  mode: ManageMode;
  contexts?: string[];
  coupons?: Coupon[];
  refetchCoupons?: () => void;
  onSaved?: () => void; // Called when coupons are successfully saved
}

interface CouponsManageContentProps {
  onClose: () => void;
  quantity: number;
  company: Company;
  mode: ManageMode;
  contexts?: string[];
  coupons?: Coupon[];
  refetchCoupons?: () => void;
  onSaved?: () => void;
  isOpen: boolean;
  setParentDirty: (dirty: boolean) => void;
}

const CouponsManageContent: React.FC<CouponsManageContentProps> = ({ onClose, quantity, company, mode, contexts, coupons: propCoupons, refetchCoupons, onSaved, isOpen, setParentDirty }) => {
  const {
    coupons,
    isSaving,
    isGenerating,
    error,
    isDirty,
    initializeCoupons,
    moveCouponDown,
    moveCouponUp,
    saveAllCoupons,
    generateCouponSuggestions,
    addEmptyCoupon,
    clearError,
  } = useCouponsManageContext();
  const confirmationModal = useConfirmationModalContext();
  const { isPreviewOpen, setCouponsToPreview, setPreviewFromModal } = usePreviewContext();
  const companyId = company._id;
  const [isLoadingAdditionalCoupon, setIsLoadingAdditionalCoupon] = useState(false);
  const wasOpenRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6, // prevents accidental drag when clicking
      },
    })
  );

  // Initialize coupons only when modal opens (not when propCoupons reference changes)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - initialize coupons
      initializeCoupons(quantity, companyId, mode, propCoupons);

      // If in GENERATE mode, trigger AI generation
      if (mode === ManageMode.GENERATE && companyId) {
        generateCouponSuggestions(companyId, quantity, contexts).catch((error) => {
          console.error('Error generating coupon suggestions:', error);
        });
      }

      wasOpenRef.current = true;
    } else if (!isOpen) {
      // Modal closed - reset flag for next time
      wasOpenRef.current = false;
    }
  }, [isOpen, quantity, companyId, mode, propCoupons, initializeCoupons, generateCouponSuggestions]);

  // Update preview when coupons change (if preview is open)
  useEffect(() => {
    if (isPreviewOpen && coupons.length > 0) {
      setCouponsToPreview(coupons);
    }
  }, [coupons, isPreviewOpen, setCouponsToPreview]);

  // Reset isAddingAdditionalCoupon when generation is complete and we have more coupons
  useEffect(() => {
    if (isLoadingAdditionalCoupon && !isGenerating && coupons.length > 0) {
      setIsLoadingAdditionalCoupon(false);
    }
  }, [isLoadingAdditionalCoupon, isGenerating, coupons.length]);

  useEffect(() => {
    setParentDirty(isDirty);
  }, [isDirty])

  const title = useMemo(() => {
    switch (mode) {
      case ManageMode.EDIT:
        return 'Redigera kuponger';
      case ManageMode.ADD:
        return 'Skapa kuponger';
      case ManageMode.GENERATE:
        return 'Generera kuponger';
      default:
        return 'Hantera kuponger';
    }
  }, [mode]);

  const handleGenerateCoupon = async () => {
    // Generate a new coupon with AI
    if (companyId) {
      setIsLoadingAdditionalCoupon(true);
      try {
        await generateCouponSuggestions(companyId, 1);
      } catch (error) {
        console.error('Error generating coupon:', error);
        setIsLoadingAdditionalCoupon(false);
      }
    }
  }

  const handleAddCoupon = async () => {
    // Add an empty coupon in editing mode
    addEmptyCoupon(companyId);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isDirty) {
      // No changes to save
      onClose();
      return;
    }

    // Only show confirmation modal when creating/generating new coupons, not when editing existing ones
    const isEditingExisting = mode === ManageMode.EDIT;

    if (!isEditingExisting) {
      // Show confirmation modal before saving new coupons
      const result = await confirmationModal.showConfirmation(
        'Bekräfta nya kuponger',
        'Är du säker på att du vill spara alla nya kuponger?'
      );

      if (!result) {
        return;
      }
    }

    try {
      await saveAllCoupons();
      // Call onSaved callback if provided (e.g. to close parent modal)
      if (onSaved) {
        onSaved();
      }
      if (refetchCoupons) {
        refetchCoupons();
      }
    } catch (error) {
      console.error('Error saving coupons:', error);
    }
  };

  const handleSave = async () => {
    if (formRef.current?.reportValidity()) {
      await saveAllCoupons();
      if (refetchCoupons) {
        console.log('Refetching coupons after saving from save button');
        refetchCoupons();
      }
    }
  };

  const handlePreview = () => {
    setPreviewFromModal(company, coupons);
  };

  const normalizeCouponPositions = (coupons: CouponWithState[]) => {
    return sortCouponsByPosition(coupons).map((coupon, index) => ({
      ...coupon,
      position: index
    }));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const normalized = normalizeCouponPositions(coupons);

    const oldIndex = normalized.findIndex(c => c.id === active.id);
    const newIndex = normalized.findIndex(c => c.id === over.id);

    const diff = newIndex - oldIndex;

    if (diff > 0) {
      // moved down
      for (let i = 0; i < diff; i++) {
        moveCouponDown(active.id as string);
      }
    } else if (diff < 0) {
      // moved up
      for (let i = 0; i < Math.abs(diff); i++) {
        moveCouponUp(active.id as string);
      }
    }
  };

  return (
    <>
      <div id='coupons-manage' className="flex items-center justify-between">
        <h4>{title}</h4>
        {mode === ManageMode.ADD && (
          <small className="text-sm">
            Tips! Dra i en kupong för att ändra dess ordning i häftet!
          </small>
        )}
        <div className="flex">
          <button
            type="button"
            className="secondary xs flex items-center"
            disabled={!isDirty}
            onClick={handleSave}
          >
            <FaRegFloppyDisk size={16} />
          </button>
          <button
            type="button"
            className="secondary xs flex items-center justify-center gap-1"
            onClick={handlePreview}
          >
            <ImEye size={16} />
            Förhandsvisa
          </button>
          <button className="flex items-center close-button" onClick={onClose}>
            <HiXMark size={24} />
          </button>
        </div>
      </div>
      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="form-content coupons-modal-content flex flex-col gap-2">
          <div className="coupons-modal-list flex flex-col gap-2">
            {error && (
              <div className="coupon-generation-error">
                <p>{error}</p>
                <button
                  type="button"
                  onClick={clearError}
                  aria-label="Stäng felmeddelande"
                >
                  ×
                </button>
              </div>
            )}
            {isGenerating && coupons.length === 0 ? (
              <>
                {Array.from({ length: quantity }).map((_, index) => (
                  <LoadingCoupon key={`loading-${index}`} />
                ))}
              </>
            ) : (
              <> {mode === ManageMode.ADD ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortCouponsByPosition(coupons).map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortCouponsByPosition(coupons).map((coupon) => (
                      <CouponCard
                        key={coupon.id}
                        coupon={coupon}
                        isDraggable
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                <>
                  {sortCouponsByPosition(coupons).map((coupon) => (
                    <CouponCard
                      key={coupon.id}
                      coupon={coupon}
                    />
                  ))}
                  {isLoadingAdditionalCoupon && isGenerating && (
                    <LoadingCoupon key="adding-additional-coupon" />
                  )}
                </>
              )}
              </>
            )}
          </div>

          {mode === ManageMode.ADD && (
            <div className="coupons-add-generate">
              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  onClick={handleGenerateCoupon}
                  disabled={isGenerating}
                  className="secondary sm"
                  aria-label='Generera kupong'
                  title='Generera kupong'
                >
                  <div className='flex items-center justify-center gap-1'>
                    <HiSparkles />
                    <span>Generera ny kupong</span>
                  </div>
                </Button>

                <Button
                  type="button"
                  onClick={handleAddCoupon}
                  disabled={isGenerating}
                  className="primary sm"
                  aria-label='Lägg till kupong'
                  title='Lägg till kupong'
                >
                  <div className='flex items-center justify-center gap-1'>
                    <HiPlus size={16} />
                    <span>Lägg till kupong</span>
                  </div>
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="form-buttons">
          <Button
            type="submit"
            className="primary md"
            loading={isSaving || isGenerating}
            disabled={isSaving || isGenerating || coupons.some(c => c.pendingApproval) || coupons.length === 0}
            title={coupons.some(c => c.pendingApproval) ? 'Du måste godkänna eller avböja pågående förslag innan du kan spara kuponger' : undefined}
          >
            <div className='flex items-center justify-center gap-1'>
              <p>Spara och stäng</p> <FaRegFloppyDisk size={16} />
            </div>
          </Button>
        </div>
      </form >
    </>
  );
}

const ModalCouponsManage: React.FC<CouponsManageProps> = (props) => {
  const { isOpen, onClose, onSaved, ...contentProps } = props;
  const { isPreviewOpen, modalCompanyPreview, setIsPreviewOpen, setCouponsToPreview, setModalCompanyPreview, resetPreview } = usePreviewContext();
  const modalClassName = `modal-coupons-form coupons-modal ${isPreviewOpen ? 'preview-open' : ''}`;
  const confirmation = useConfirmationModalContext();
  // Wheter coupons have been edited in this modal and need to be saved
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    //close the preview modal if its open already
    if (isOpen && isPreviewOpen) {
      resetPreview();
      setIsPreviewOpen(false);
    }

  }, [])

  const closeModals = (saved = false) => {
    if (modalCompanyPreview) {
      setIsPreviewOpen(false);
      setCouponsToPreview(null);
      setModalCompanyPreview(null);
    }
    onClose();
    if (saved && onSaved) {
      onSaved();
    }
  };

  // Wrap onClose to also clean up preview
  const handleClose = async () => {
    if (dirty) {
      const r = await confirmation.showConfirmation(
        'Vill du stänga utan att spara?',
        'Alla eventuella änndringar går förlorade.',
      );

      if (!r) return;
    }


    closeModals();
  };

  return (
    <Modal isOpen={isOpen} closeModal={handleClose} className={modalClassName} showClose={false}>
      <CouponsManageProvider>
        <CouponsManageContent {...contentProps} onClose={handleClose} onSaved={() => closeModals(true)} isOpen={isOpen} setParentDirty={setDirty} />
      </CouponsManageProvider>
    </Modal>
  );
}

export default ModalCouponsManage;

